/**
 * Canonical debug-fixture registry.
 *
 * Stores fixtures keyed by `(elementType, variant, schemaVersion)` and
 * exposes deterministic accessors so callers can build debug URLs without
 * touching production request normalisation.
 *
 * `registerFixture` performs shape validation up-front so that misconfigured
 * fixtures fail loudly at import time rather than during a plugin session.
 */

import {
    CanonicalFixture,
    FIXTURE_VARIANTS,
    FixtureLookupOptions,
    FixtureVariant,
} from './types';

type ElementBucket = Map<FixtureVariant, Map<string, CanonicalFixture>>;

const registry: Map<string, ElementBucket> = new Map();

function normaliseElementType(elementType: string): string {
    return elementType.trim().toLowerCase();
}

function assertKnownVariant(variant: string): asserts variant is FixtureVariant {
    if (!FIXTURE_VARIANTS.includes(variant as FixtureVariant)) {
        throw new Error(
            `Unknown fixture variant "${variant}". Expected one of: ${FIXTURE_VARIANTS.join(', ')}.`,
        );
    }
}

export function registerFixture(fixture: CanonicalFixture): void {
    if (!fixture.elementType || !fixture.elementType.trim()) {
        throw new Error('registerFixture: elementType is required.');
    }
    assertKnownVariant(fixture.variant);
    if (!fixture.schemaVersion) {
        throw new Error(
            `registerFixture(${fixture.elementType}/${fixture.variant}): schemaVersion is required.`,
        );
    }
    if (!fixture.compatibleSchemaVersions.includes(fixture.schemaVersion)) {
        throw new Error(
            `registerFixture(${fixture.elementType}/${fixture.variant}): schemaVersion "${fixture.schemaVersion}" must appear in compatibleSchemaVersions.`,
        );
    }
    if (!fixture.payload || typeof fixture.payload !== 'object') {
        throw new Error(
            `registerFixture(${fixture.elementType}/${fixture.variant}): payload is required.`,
        );
    }

    const key = normaliseElementType(fixture.elementType);
    let bucket = registry.get(key);
    if (!bucket) {
        bucket = new Map();
        registry.set(key, bucket);
    }
    let versions = bucket.get(fixture.variant);
    if (!versions) {
        versions = new Map();
        bucket.set(fixture.variant, versions);
    }
    for (const compat of fixture.compatibleSchemaVersions) {
        versions.set(compat, fixture);
    }
}

/**
 * True when an element type has any registered fixture (any variant, any
 * version). Used to decide whether to override the legacy mock catalogue.
 */
export function hasRegisteredElement(elementType: string): boolean {
    return registry.has(normaliseElementType(elementType));
}

/**
 * List every element type registered with at least one fixture, sorted for
 * deterministic snapshots.
 */
export function listRegisteredElementTypes(): readonly string[] {
    return Array.from(registry.keys()).sort();
}

/**
 * List every variant registered for a given element type.
 */
export function listRegisteredVariants(elementType: string): readonly FixtureVariant[] {
    const bucket = registry.get(normaliseElementType(elementType));
    if (!bucket) {
        return [];
    }
    return FIXTURE_VARIANTS.filter(v => bucket.has(v));
}

/**
 * List every schema version registered for a given element type (across all
 * variants), sorted lexicographically.
 */
export function listRegisteredSchemaVersions(elementType: string): readonly string[] {
    const bucket = registry.get(normaliseElementType(elementType));
    if (!bucket) {
        return [];
    }
    const versions = new Set<string>();
    for (const versionMap of Array.from(bucket.values())) {
        for (const version of Array.from(versionMap.keys())) {
            versions.add(version);
        }
    }
    return Array.from(versions).sort();
}

function pickLatestSchemaVersion(versions: Map<string, CanonicalFixture>): string | undefined {
    // Semver-aware sort with lexicographic fallback for non-semver strings.
    const keys = Array.from(versions.keys());
    if (keys.length === 0) {
        return undefined;
    }
    keys.sort((a, b) => {
        const parseSemver = (v: string) => v.split('.').map(part => Number.parseInt(part, 10));
        const aParts = parseSemver(a);
        const bParts = parseSemver(b);
        if (aParts.every(Number.isFinite) && bParts.every(Number.isFinite)) {
            for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
                const diff = (aParts[i] ?? 0) - (bParts[i] ?? 0);
                if (diff !== 0) return diff;
            }
            return 0;
        }
        return a < b ? -1 : a > b ? 1 : 0;
    });
    return keys[keys.length - 1];
}

function buildUnsupportedVersionFixture(
    elementType: string,
    requestedVersion: string,
    supportedVersions: readonly string[],
    referenceFixture: CanonicalFixture,
): CanonicalFixture {
    const versionSlug = requestedVersion.replace(/[^A-Za-z0-9._:-]/g, '-');
    return {
        elementType,
        variant: 'unsupported-version',
        schemaVersion: requestedVersion,
        compatibleSchemaVersions: [requestedVersion],
        wrapperContext: referenceFixture.wrapperContext,
        payload: {
            ...referenceFixture.payload,
            schemaVersion: requestedVersion,
            packageId: `pkg-web-debug-${elementType}-unsupported-${versionSlug}`,
            runId: `run-web-debug-${elementType}-unsupported-${versionSlug}`,
        },
        defaultElementType: referenceFixture.defaultElementType,
        metadata: {
            description: `Canonical package schema version "${requestedVersion}" is transported for compatibility testing and is expected to be rejected by a package-aware validator. Supported versions: ${supportedVersions.join(', ') || '(none)'}.`,
        },
    };
}

function findReferenceFixture(bucket: ElementBucket): CanonicalFixture | undefined {
    for (const variants of Array.from(bucket.values())) {
        for (const fixture of Array.from(variants.values())) {
            return fixture;
        }
    }
    return undefined;
}

/**
 * Fetch a fixture for `(elementType, variant, schemaVersion?)`. When
 * `schemaVersion` is omitted the newest registered version wins.
 *
 * Returns:
 *   - the matching fixture when the element type is registered, the variant
 *     is registered, and either no version was requested or the requested
 *     version is in `compatibleSchemaVersions`.
 *   - a synthetic `unsupported-version` fixture when the element type is
 *     registered but the requested `schemaVersion` is not compatible with any
 *     registered variant (unless `synthesiseUnsupportedVersion: false`).
 *   - `undefined` when the element type or variant is not registered.
 */
export function getFixture(
    elementType: string,
    variant: FixtureVariant,
    options: FixtureLookupOptions = {},
): CanonicalFixture | undefined {
    assertKnownVariant(variant);
    const key = normaliseElementType(elementType);
    const bucket = registry.get(key);
    if (!bucket) {
        return undefined;
    }

    if (variant === 'unsupported-version') {
        if (!options.schemaVersion) {
            return undefined;
        }
        const reference = findReferenceFixture(bucket);
        return reference
            ? buildUnsupportedVersionFixture(
                  elementType,
                  options.schemaVersion,
                  listRegisteredSchemaVersions(elementType),
                  reference,
              )
            : undefined;
    }

    const versionMap = bucket.get(variant);
    if (!versionMap || versionMap.size === 0) {
        return undefined;
    }

    if (options.schemaVersion) {
        const match = versionMap.get(options.schemaVersion);
        if (match) {
            return match;
        }
        if (options.synthesiseUnsupportedVersion === false) {
            return undefined;
        }
        // Prefer a reference fixture from the same variant to keep the label
        // and canonical element `type` consistent with the caller's request;
        // fall back to any registered fixture for the element.
        const reference = versionMap.values().next().value as CanonicalFixture | undefined
            ?? findReferenceFixture(bucket);
        if (!reference) {
            return undefined;
        }
        return buildUnsupportedVersionFixture(
            elementType,
            options.schemaVersion,
            listRegisteredSchemaVersions(elementType),
            reference,
        );
    }

    const latest = pickLatestSchemaVersion(versionMap);
    return latest ? versionMap.get(latest) : undefined;
}

/**
 * Test-only: clear the entire registry. Not exported from `index.ts`.
 * Callers must import it explicitly from `registry` in test files.
 */
export function __resetRegistryForTests(): void {
    registry.clear();
}
